import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_SIZE_CARD_DATA } from "../../DataTagConstants";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import "./DataEstateSizeCard.scss";
import { percentageCalculation } from "../../../../components/common/utilities";
import { filesize } from "filesize";

const formatSize = (bytes) => {
  return filesize(bytes, { standard: "jedec" });
};

const calcPercent = (value, total) =>
  total > 0 ? ((value / total) * 100).toFixed(1) : 0;

function DataEstateSizeCard() {
  const sizeData = useSelector((state) => state.dataTagReducer.sizeCardData);
  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (dataSourceId) {
      dispatch({ type: FETCH_SIZE_CARD_DATA, payload: dataSourceId });
    }
  }, [dataSourceId]);

  if (!sizeData) return <div>Loading...</div>;

  const sizeMB = sizeData?.size?.[0]?.["DataTagSensitivity.fileSizeTotal"] || 0;

  const pdaCount =
    sizeData?.pda?.[0]?.["DataTagPDA.publicAccessCheckedCount"] || 0;

  const regulatedCount = 
    sizeData?.regulatedData?.[0]?.["DataTagSensitivity.regulatedCount"] || 0;

  const sensitiveCount =
    sizeData?.sensitiveData?.[0]?.["DataTagSensitivity.count"] || 0;

  const totalCount = sizeData?.total?.[0]?.["DataTagSensitivity.count"] || 1;

  const formattedSize = formatSize(sizeMB);

  const pdaPercent =
    percentageCalculation([{ count: pdaCount }], totalCount)[0]?.count || 0;
  const regulatedPercent =
    percentageCalculation([{ count: regulatedCount }], totalCount)[0]?.count || 0;
  const sensitivePercent =
    percentageCalculation([{ count: sensitiveCount }], totalCount)[0]?.count ||
    0;

  const doughnutData = {
    datasets: [
      {
        labels: ["Regulated", "Remaining"],
        data: [regulatedPercent, 100 - regulatedPercent],
        backgroundColor: ["#ffa500", "#e5e5e5"],
        weight: 1,
        spacing: 4,
      },
      {
        labels: ["PDA", "Remaining"],
        data: [pdaPercent, 100 - pdaPercent],
        backgroundColor: ["#00aaff", "#e5e5e5"],
        weight: 1,
        spacing: 4,
      },
      {
        labels: ["Sensitive", "Remaining"],
        backgroundColor: ["#ff3366", "#e5e5e5"],
        data: [sensitivePercent, 100 - sensitivePercent],
        weight: 1,
        spacing: 4,
      },
    ],
  };

  return (
    <div className="size-card">
      <div className="card-header size">
        <div>Size</div>
        <div className="size-display">{formattedSize}</div>
      </div>

      <div className="sizeCardLowerContainer">
        <div className="details">
          <div className="row">
            <div className="circle total"></div>
            <div>
              <span>Total</span>
              <strong>{Number(totalCount || 0).toLocaleString()}</strong>
            </div>
          </div>

          <div className="row">
            <div className="circle regulated"></div>
            <div>
              <span>Regulated</span>
              <strong>{regulatedPercent}%</strong>
            </div>
          </div>

          <div className="row">
            <div className="circle pda"></div>
            <div>
              <span>Public</span>
              <strong>{pdaPercent}%</strong>
            </div>
          </div>

          <div className="row">
            <span className="circle sensitivity"></span>
            <div>
              <span>Sensitive</span>
              <strong>{sensitivePercent}%</strong>
            </div>
          </div>
        </div>

        <div className="chart">
          <Doughnut
            data={doughnutData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                datalabels: { display: false },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const label =
                        context.dataset.labels?.[context.dataIndex] || "";

                      const datasetIndex = context.datasetIndex;

                      let value = 0;

                      if (datasetIndex === 0) {
                        if (label === "Regulated") value = regulatedCount;
                        if (label === "Remaining")
                          value = totalCount - regulatedCount;
                      }

                      if (datasetIndex === 1) {
                        if (label === "PDA") value = pdaCount;
                        if (label === "Remaining")
                          value = totalCount - pdaCount;
                      }

                      if (datasetIndex === 2) {
                        if (label === "Sensitive") value = sensitiveCount;
                        if (label === "Remaining")
                          value = totalCount - sensitiveCount;
                      }

                      return `${label}: ${value}`;
                    },
                  },
                },
              },

              hover: {
                mode: null,
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default DataEstateSizeCard;
